import { useMemo } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import type { AggregatedDate } from '@/types';
import type { DateRangePreset } from '@/types';

interface SentimentChartProps {
  data: AggregatedDate[];
  preset: DateRangePreset;
  countBy?: 'channel' | 'ticker';  // What to count: channels (bloggers) or tickers
}

// Sentiment colors matching the card design
const COLORS = {
  bull: '#2AA84A',
  neutral: '#9AA4B2',
  bear: '#FF6A00',
};

// Format date for display on x-axis
function formatDateLabel(dateStr: string, short = false): string {
  const date = new Date(dateStr);
  const month = date.getMonth() + 1;
  const day = date.getDate();
  return short ? `${month}/${day}` : `${month}月${day}日`;
}

// Get week number of the year
function getWeekNumber(date: Date): number {
  const firstDayOfYear = new Date(date.getFullYear(), 0, 1);
  const pastDaysOfYear = (date.getTime() - firstDayOfYear.getTime()) / 86400000;
  return Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7);
}

// Format week label
function formatWeekLabel(year: number, week: number): string {
  return `${year}年第${week}周`;
}

interface ChartDataPoint {
  label: string;
  bull: number;
  neutral: number;
  bear: number;
}

// Count unique tickers in a ViewpointEntry array
function countUniqueTickers(entries: { ticker?: string }[]): number {
  const tickers = new Set<string>();
  for (const entry of entries) {
    if (entry.ticker) {
      tickers.add(entry.ticker);
    }
  }
  return tickers.size;
}

// Aggregate data by week for 3-month view
function aggregateByWeek(data: AggregatedDate[], countBy: 'channel' | 'ticker'): ChartDataPoint[] {
  const weekMap = new Map<string, {
    bull: number;
    neutral: number;
    bear: number;
    year: number;
    week: number;
    bullTickers: Set<string>;
    neutralTickers: Set<string>;
    bearTickers: Set<string>;
  }>();

  for (const item of data) {
    const date = new Date(item.date);
    const year = date.getFullYear();
    const week = getWeekNumber(date);
    const key = `${year}-${week}`;

    if (!weekMap.has(key)) {
      weekMap.set(key, {
        bull: 0,
        neutral: 0,
        bear: 0,
        year,
        week,
        bullTickers: new Set(),
        neutralTickers: new Set(),
        bearTickers: new Set(),
      });
    }

    const entry = weekMap.get(key)!;
    if (countBy === 'ticker') {
      // Collect unique tickers for later counting
      for (const vp of item.bull) {
        if (vp.ticker) entry.bullTickers.add(vp.ticker);
      }
      for (const vp of item.neutral) {
        if (vp.ticker) entry.neutralTickers.add(vp.ticker);
      }
      for (const vp of item.bear) {
        if (vp.ticker) entry.bearTickers.add(vp.ticker);
      }
    } else {
      entry.bull += item.bull.length;
      entry.neutral += item.neutral.length;
      entry.bear += item.bear.length;
    }
  }

  // Convert to array and sort by year/week
  const result = Array.from(weekMap.entries())
    .sort((a, b) => {
      if (a[1].year !== b[1].year) return a[1].year - b[1].year;
      return a[1].week - b[1].week;
    })
    .map(([_, value]) => ({
      label: formatWeekLabel(value.year, value.week),
      bull: countBy === 'ticker' ? value.bullTickers.size : value.bull,
      neutral: countBy === 'ticker' ? value.neutralTickers.size : value.neutral,
      bear: countBy === 'ticker' ? value.bearTickers.size : value.bear,
    }));

  return result;
}

// Transform daily data for chart
function transformDailyData(data: AggregatedDate[], countBy: 'channel' | 'ticker'): ChartDataPoint[] {
  // Sort by date ascending
  const sorted = [...data].sort((a, b) => a.date.localeCompare(b.date));

  return sorted.map((item) => ({
    label: formatDateLabel(item.date, true),
    bull: countBy === 'ticker' ? countUniqueTickers(item.bull) : item.bull.length,
    neutral: countBy === 'ticker' ? countUniqueTickers(item.neutral) : item.neutral.length,
    bear: countBy === 'ticker' ? countUniqueTickers(item.bear) : item.bear.length,
  }));
}

export default function SentimentChart({ data, preset, countBy = 'channel' }: SentimentChartProps) {
  const theme = useTheme();

  const chartData = useMemo(() => {
    if (data.length === 0) return [];

    // Use weekly aggregation for 3-month view, daily for others
    if (preset === '3months') {
      return aggregateByWeek(data, countBy);
    }
    return transformDailyData(data, countBy);
  }, [data, preset, countBy]);

  // Dynamic title based on what we're counting
  const chartTitle = countBy === 'ticker' ? '标的情绪分布' : '博主情绪分布';

  if (chartData.length === 0) {
    return null;
  }

  const labels = chartData.map((d) => d.label);
  const bullData = chartData.map((d) => d.bull);
  const neutralData = chartData.map((d) => d.neutral);
  const bearData = chartData.map((d) => d.bear);

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
        {chartTitle}
      </Typography>
      <Box
        sx={{
          bgcolor: 'background.paper',
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          p: 2,
        }}
      >
        <BarChart
          xAxis={[
            {
              scaleType: 'band',
              data: labels,
              tickLabelStyle: {
                fontSize: 11,
                fill: theme.palette.text.secondary,
              },
            },
          ]}
          yAxis={[
            {
              tickLabelStyle: {
                fontSize: 11,
                fill: theme.palette.text.secondary,
              },
            },
          ]}
          series={[
            {
              data: bullData,
              stack: 'sentiment',
              label: '看涨',
              color: COLORS.bull,
            },
            {
              data: neutralData,
              stack: 'sentiment',
              label: '观望',
              color: COLORS.neutral,
            },
            {
              data: bearData,
              stack: 'sentiment',
              label: '看跌',
              color: COLORS.bear,
            },
          ]}
          height={250}
          margin={{ left: 40, right: 20, top: 20, bottom: 30 }}
          slotProps={{
            legend: {
              position: { vertical: 'top', horizontal: 'end' },
            },
          }}
          sx={{
            // Make bars slightly rounded
            '& .MuiBarElement-root': {
              rx: 2,
            },
          }}
        />
      </Box>
    </Box>
  );
}
